import { CurrencyInput } from '@/components/ui/currency-input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PDKArrowUpDown, PDKCheck } from '@/components/ui/pdk-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import useLocalStorage from '@/hooks/use-local-storage';
import { centsToInitialValue } from '@/lib/currency';
import type { Deal, Payment } from '@/models/registry';
import { isEmpty } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import AddPayment from './add-payment';
import DealPaymentsOwed from './deal-payments-owed';
import PaymentLine from './payment-line';
import {
  sortPaymentsByCreatedNewest,
  sortPaymentsByCreatedOldest,
} from './sort-payment';

type SortValue = 'created_newest' | 'created_oldest';

const DealPayments = observer(({ deal }: { deal: Deal }) => {
  const payments = deal.payments ?? [];
  const paymentGroups = useMemo(() => {
    const customer = payments.filter(
      (payment) => payment.type !== 'funding_amount',
    );
    const loan = payments.filter(
      (payment) => payment.type === 'funding_amount',
    );
    return { customer, loan };
  }, [payments]);
  const hasPayments = !isEmpty(deal.payments);
  const [sortBy, setSortBy] = useLocalStorage<SortValue>(
    'dealPayments.sortBy',
    'created_oldest',
  );

  const sortedPayments = useCallback(
    (list: Payment[]) => {
      switch (sortBy) {
        case 'created_newest':
          return sortPaymentsByCreatedNewest(list);
        case 'created_oldest':
          return sortPaymentsByCreatedOldest(list);
        default:
          return list;
      }
    },
    [sortBy],
  );

  const sortedCustomer = useMemo(
    () => sortedPayments(paymentGroups.customer),
    [paymentGroups.customer, sortedPayments],
  );

  const sortedLoan = useMemo(
    () => sortedPayments(paymentGroups.loan),
    [paymentGroups.loan, sortedPayments],
  );

  return (
    <div className="flex flex-col gap-0 shrink-0">
      <div className="flex flex-row gap-2 items-center flex-wrap">
        <h5 className="scroll-m-20 text-lg font-semibold tracking-tight">
          Payments
        </h5>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-auto cursor-pointer">
              <PDKArrowUpDown className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel className="text-sm text-muted-foreground">
              Sort by
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                setSortBy('created_newest');
              }}
            >
              <span>Created - Newest</span>
              {sortBy === 'created_newest' && (
                <PDKCheck className="h-3.5 w-3.5" />
              )}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                setSortBy('created_oldest');
              }}
            >
              <span>Created - Oldest</span>
              {sortBy === 'created_oldest' && (
                <PDKCheck className="h-3.5 w-3.5" />
              )}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {hasPayments && (
        <div className="flex flex-row gap-2 items-center flex-nowrap justify-between">
          <div className="flex flex-row gap-2 items-center flex-nowrap cursor-pointer">
            <div className="w-22.5 text-sm">Total:</div>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <CurrencyInput
                    value={centsToInitialValue(
                      deal.payments?.reduce((acc, payment) => {
                        if (payment.deleted_at) {
                          return acc;
                        }

                        return acc + payment.amount;
                      }, 0),
                    )}
                    readOnly={true}
                    placeholder={''}
                    className="text-uneditable  !text-[13px] text-right w-16"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                The total amount for all payments (excluding deleted payments)
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex flex-row gap-2 items-center flex-nowrap cursor-pointer">
            <div className="w-22.5 text-sm">Owed:</div>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <DealPaymentsOwed payments={deal.payments} />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                The total amount owed for all payments that have not been
                confirmed (excluding deleted payments)
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      )}
      {sortedCustomer.length > 0 && (
        <>
          <div className="text-sm font-semibold">Customer Payments</div>
          {sortedCustomer.map((payment) => (
            <PaymentLine key={payment.id} payment={payment} dealId={deal.id} />
          ))}
        </>
      )}

      {sortedLoan.length > 0 && (
        <>
          <div className="text-sm font-semibold">Loan / Funding</div>
          {sortedLoan.map((payment) => (
            <PaymentLine key={payment.id} payment={payment} dealId={deal.id} />
          ))}
        </>
      )}

      <div className="flex flex-row gap-2 items-center flex-wrap justify-between md:justify-normal">
        <AddPayment dealId={deal.id} />
      </div>
    </div>
  );
});

export default DealPayments;
